import React from 'react';
import {
  UpcomingAppointments,
  PendingTodos,
  CompletedTodos,
  TodaysAppointments,
  RecentClients,
  RecentEmails,
  AppointmentsByStatus,
  EmailSyncStatus,
} from './components';
import StatCardWidget from './components/StatCardWidget';
import AiNextActionsWidget from './components/AiNextActionsWidget';
import CombinedCalendarWidget from './components/CombinedCalendarWidget';
import SystemHealthStatWidget from './components/system-health/SystemHealthStatWidget';
import SystemHealthOpsSummaryWidget from './components/system-health/SystemHealthOpsSummaryWidget';
import SystemHealthResourcesWidget from './components/system-health/SystemHealthResourcesWidget';
import RecentSyncActivityWidget from './components/system-health/RecentSyncActivityWidget';
import LiveSyncLogWidget from './components/system-health/LiveSyncLogWidget';
import SyncStatsWidget from './components/system-health/SyncStatsWidget';
import type { SystemHealthMetricKey } from './components/system-health/SystemHealthStatWidget';

export type WidgetCategory = 'stats' | 'charts' | 'lists' | 'calendar';

export interface WidgetSettingsSchema {
  limit?: { type: 'number'; default: number; min?: number; max?: number };
  dateRange?: { type: 'string'; default: string };
  [key: string]: unknown;
}

/** Which dashboard filters (time range / scope) this widget respects */
export interface DashboardFilterSupport {
  timeRange?: boolean;
  scope?: boolean;
}

export interface WidgetDefinition {
  id: string;
  name: string;
  description: string;
  category: WidgetCategory;
  component: React.ComponentType<any>;
  defaultSize: { w: number; h: number };
  defaultSettings: Record<string, unknown>;
  settingsSchema?: WidgetSettingsSchema;
  minSize?: { w: number; h: number };
  maxSize?: { w: number; h: number };
  /** When set, this widget uses the dashboard Time range and/or Scope filters */
  dashboardFilterSupport?: DashboardFilterSupport;
}

const statCardMetricEntries: { id: string; name: string; description: string; metricKey: string }[] = [
  { id: 'stat-total-clients', name: 'Total organizations', description: 'Organizations in your address book', metricKey: 'totalClients' },
  { id: 'stat-pending-todos', name: 'Pending todos', description: 'Count of pending todos', metricKey: 'pendingTodos' },
  { id: 'stat-new-clients-this-month', name: 'New organizations this month', description: 'Organizations added this month', metricKey: 'newClientsThisMonth' },
  { id: 'stat-missed-appointments', name: 'Missed appointments', description: 'Count of missed appointments', metricKey: 'missedAppointments' },
  { id: 'stat-unread-emails-count', name: 'Unread emails', description: 'Count of unread emails', metricKey: 'unreadEmailsCount' },
];

const systemHealthStatEntries: { id: string; name: string; description: string; metricKey: SystemHealthMetricKey }[] = [
  { id: 'stat-system-status', name: 'System status', description: 'Overall server health', metricKey: 'overallStatus' },
  { id: 'stat-disk-free', name: 'Disk free', description: 'Available disk space on root volume', metricKey: 'diskFree' },
  { id: 'stat-email-sync-health', name: 'Email sync health', description: 'Healthy email accounts vs total', metricKey: 'emailSyncHealth' },
  { id: 'stat-failed-logins', name: 'Failed logins (24h)', description: 'Failed login attempts in the last 24 hours', metricKey: 'failedLogins' },
  { id: 'stat-online-users', name: 'Online users', description: 'Users currently connected', metricKey: 'onlineUsers' },
];

const systemHealthStatSettingsSchema: WidgetSettingsSchema = {
  metricKey: {
    type: 'string',
    default: 'overallStatus',
    enum: ['overallStatus', 'diskFree', 'emailSyncHealth', 'failedLogins', 'onlineUsers'],
  },
};

const statCardSettingsSchema: WidgetSettingsSchema = {
  metricKey: {
    type: 'string',
    default: 'pendingTodos',
    enum: [
      'totalClients', 'pendingTodos',
      'newClientsThisMonth', 'missedAppointments', 'unreadEmailsCount',
    ],
  },
};

const registry: WidgetDefinition[] = [
  ...statCardMetricEntries.map(({ id, name, description, metricKey }) => ({
    id,
    name,
    description,
    category: 'stats' as const,
    component: StatCardWidget,
    defaultSize: { w: 1, h: 1 },
    defaultSettings: { metricKey },
    settingsSchema: statCardSettingsSchema,
    ...(id === 'stat-pending-todos' && { dashboardFilterSupport: { scope: true } as DashboardFilterSupport }),
  })),
  ...systemHealthStatEntries.map(({ id, name, description, metricKey }) => ({
    id,
    name,
    description,
    category: 'stats' as const,
    component: SystemHealthStatWidget,
    defaultSize: { w: 1, h: 1 },
    defaultSettings: { metricKey },
    settingsSchema: systemHealthStatSettingsSchema,
  })),
  { id: 'upcoming-appointments', name: 'Upcoming appointments', description: 'List of upcoming appointments', category: 'lists', component: UpcomingAppointments, defaultSize: { w: 2, h: 2 }, defaultSettings: { limit: 5 }, settingsSchema: { limit: { type: 'number', default: 5, min: 1, max: 100 } }, dashboardFilterSupport: { timeRange: true, scope: true } },
  { id: 'todays-appointments', name: "Today's appointments", description: "Today's appointments", category: 'lists', component: TodaysAppointments, defaultSize: { w: 2, h: 2 }, defaultSettings: { limit: 5 }, settingsSchema: { limit: { type: 'number', default: 5, min: 1, max: 100 } } },
  { id: 'pending-todos', name: 'Pending todos', description: 'Pending todos list', category: 'lists', component: PendingTodos, defaultSize: { w: 2, h: 2 }, defaultSettings: { limit: 5 }, settingsSchema: { limit: { type: 'number', default: 5, min: 1, max: 100 } } },
  { id: 'completed-todos', name: 'Completed todos', description: 'Recently completed todos', category: 'lists', component: CompletedTodos, defaultSize: { w: 2, h: 2 }, defaultSettings: { limit: 5 }, settingsSchema: { limit: { type: 'number', default: 5, min: 1, max: 100 } } },
  { id: 'recent-clients', name: 'Recent organizations', description: 'Recently added organizations', category: 'lists', component: RecentClients, defaultSize: { w: 2, h: 2 }, defaultSettings: { limit: 5 }, settingsSchema: { limit: { type: 'number', default: 5, min: 1, max: 100 } } },
  { id: 'recent-emails', name: 'Recent emails', description: 'Recent emails', category: 'lists', component: RecentEmails, defaultSize: { w: 2, h: 2 }, defaultSettings: { limit: 5 }, settingsSchema: { limit: { type: 'number', default: 5, min: 1, max: 100 } } },
  {
    id: 'ai-next-actions',
    name: 'AI next actions',
    description: 'AI-suggested prioritized next steps from your workload',
    category: 'lists',
    component: AiNextActionsWidget,
    defaultSize: { w: 2, h: 2 },
    defaultSettings: {},
  },
  {
    id: 'email-sync-status',
    name: 'Email sync status',
    description: 'Email account sync health, last sync, schedule, and cached mail counts',
    category: 'lists',
    component: EmailSyncStatus,
    defaultSize: { w: 2, h: 3 },
    defaultSettings: { limit: 5 },
    settingsSchema: { limit: { type: 'number', default: 5, min: 1, max: 10 } },
  },
  {
    id: 'system-health-ops-summary',
    name: 'Ops summary',
    description: 'At-a-glance server, sync, and security status',
    category: 'stats',
    component: SystemHealthOpsSummaryWidget,
    defaultSize: { w: 3, h: 1 },
    defaultSettings: {},
  },
  {
    id: 'system-health-resources',
    name: 'Resource trends',
    description: 'Disk and memory usage sparklines',
    category: 'charts',
    component: SystemHealthResourcesWidget,
    defaultSize: { w: 2, h: 2 },
    defaultSettings: {},
  },
  {
    id: 'recent-sync-activity',
    name: 'Recent sync activity',
    description: 'Latest email sync runs across all accounts',
    category: 'lists',
    component: RecentSyncActivityWidget,
    defaultSize: { w: 2, h: 2 },
    defaultSettings: { limit: 5 },
    settingsSchema: { limit: { type: 'number', default: 5, min: 1, max: 15 } },
  },
  {
    id: 'live-sync-log',
    name: 'Live sync log',
    description: 'Real-time backend sync log stream (SSE)',
    category: 'lists',
    component: LiveSyncLogWidget,
    defaultSize: { w: 2, h: 3 },
    defaultSettings: { maxLines: 150 },
    settingsSchema: { maxLines: { type: 'number', default: 150, min: 50, max: 300 } },
  },
  {
    id: 'sync-stats-24h',
    name: 'Sync statistics (24h)',
    description: 'Email sync run counts, last successful sync, and average duration',
    category: 'stats',
    component: SyncStatsWidget,
    defaultSize: { w: 2, h: 2 },
    defaultSettings: {},
  },
  { id: 'appointments-by-status', name: 'Appointments by status', description: 'Appointments grouped by status', category: 'charts', component: AppointmentsByStatus, defaultSize: { w: 3, h: 2 }, defaultSettings: {} },
  { id: 'calendar-combined', name: 'Calendar', description: 'Month calendar of appointments and todos', category: 'calendar', component: CombinedCalendarWidget, defaultSize: { w: 2, h: 3 }, defaultSettings: {} },
];

export const getWidgetDefinition = (id: string): WidgetDefinition | undefined =>
  registry.find((w) => w.id === id);

export const getAllWidgetDefinitions = (): WidgetDefinition[] => [...registry];

/** All widgets are available to any user with dashboard access. */
export const getVisibleWidgetDefinitions = (): WidgetDefinition[] => getAllWidgetDefinitions();

export const getWidgetsByCategory = (category: WidgetCategory): WidgetDefinition[] =>
  registry.filter((w) => w.category === category);

/** Widgets that use the dashboard Time range and/or Scope filters */
export const getWidgetsUsingDashboardFilters = (): WidgetDefinition[] =>
  registry.filter((w) => w.dashboardFilterSupport && (w.dashboardFilterSupport.timeRange || w.dashboardFilterSupport.scope));

export const usesDashboardFilters = (widgetType: string): boolean => {
  const def = getWidgetDefinition(widgetType);
  return !!(def?.dashboardFilterSupport && (def.dashboardFilterSupport.timeRange || def.dashboardFilterSupport.scope));
};

export const getDefaultSettings = (widgetType: string): Record<string, unknown> => {
  const def = getWidgetDefinition(widgetType);
  return def ? { ...def.defaultSettings } : {};
};

export const isKnownWidgetType = (widgetType: string): boolean =>
  registry.some((w) => w.id === widgetType);

export interface DashboardFiltersPayload {
  timeRange?: string;
  scope?: 'myWork' | 'team';
}

export const createWidgetElement = (
  widgetType: string,
  settings: Record<string, unknown>,
  isEditing?: boolean,
  dashboardFilters?: DashboardFiltersPayload | null
): React.ReactNode => {
  const def = getWidgetDefinition(widgetType);
  if (!def) return null;
  const merged = {
    ...def.defaultSettings,
    ...settings,
    isEditing,
    embedded: true,
    ...(dashboardFilters && {
      timeRange: dashboardFilters.timeRange,
      scope: dashboardFilters.scope,
    }),
  };
  const Component = def.component;
  return <Component {...merged} />;
};

export default registry;
